import { CHUNKS_STORE, DB_NAME, IDB_VERSION, LAST_WRITTEN_AT_INDEX, SESSIONS_STORE } from './constants'
import type { ChunkRecord, SessionRecord } from './types'

// Promise wrappers around the IndexedDB API used by `TranscriptDocCache`.
// Reject on `onerror`; callers decide whether to fall through to a cache miss.

export const openCacheDb = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, IDB_VERSION)

        request.onupgradeneeded = () => {
            const db = request.result

            // Composite primary key so chunks for one session are contiguous
            // and queryable with `IDBKeyRange.bound([key, 0], [key, Infinity])`.
            // No indexes -- range scans use the key.
            db.createObjectStore(CHUNKS_STORE, { keyPath: ['sessionKey', 'sequence'] })

            const sessions = db.createObjectStore(SESSIONS_STORE, { keyPath: 'sessionKey' })
            sessions.createIndex(LAST_WRITTEN_AT_INDEX, 'lastWrittenAt', { unique: false })
        }

        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
        request.onblocked = () => reject(new Error('IndexedDB open blocked'))
    })
}

/**
 * Wraps a `readwrite` transaction's lifecycle in a promise so the four
 * mutating helpers below share one source of truth for `oncomplete` /
 * `onerror` / `onabort` handling. `enqueue` runs synchronously with the
 * open transaction; throwing from it surfaces as a rejected promise.
 * On a synchronous throw, `tx.abort()` rolls back any requests already
 * queued so a rejection means nothing from this transaction persisted.
 */
const runWriteTransaction = (db: IDBDatabase, stores: string | string[], enqueue: (tx: IDBTransaction) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(stores, 'readwrite')
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'))

        try {
            enqueue(tx)
        } catch (error) {
            try {
                tx.abort()
            } catch {
                // Transaction may already be finished.
            }
            reject(error)
        }
    })
}

export const getSession = async (db: IDBDatabase, sessionKey: string): Promise<SessionRecord | null> => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(SESSIONS_STORE, 'readonly')
        const store = tx.objectStore(SESSIONS_STORE)
        const request = store.get(sessionKey)

        request.onsuccess = () => resolve((request.result as SessionRecord | undefined) ?? null)
        request.onerror = () => reject(request.error)
    })
}

/**
 * Returns every chunk for `sessionKey` in sequence-ascending order. Uses a
 * single cursor walk so we issue one IDB transaction regardless of chunk
 * count.
 */
export const getChunks = async (db: IDBDatabase, sessionKey: string): Promise<Uint8Array[]> => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(CHUNKS_STORE, 'readonly')
        const store = tx.objectStore(CHUNKS_STORE)
        const range = IDBKeyRange.bound([sessionKey, -Infinity], [sessionKey, Infinity])
        const request = store.openCursor(range, 'next')

        const updates: Uint8Array[] = []
        request.onsuccess = () => {
            const cursor = request.result
            if (!cursor) {
                resolve(updates)

                return
            }
            const record = cursor.value as ChunkRecord
            updates.push(record.update)
            cursor.continue()
        }
        request.onerror = () => reject(request.error)
    })
}

/**
 * Upserts a session row without touching its chunks. Used to bump
 * `lastWrittenAt` after a hydrate hit so a freshly-hydrated session stays at
 * the top of the LRU list.
 */
export const putSession = (db: IDBDatabase, session: SessionRecord): Promise<void> =>
    runWriteTransaction(db, SESSIONS_STORE, (tx) => {
        tx.objectStore(SESSIONS_STORE).put(session)
    })

/**
 * Appends one new chunk and upserts the session row in a single transaction.
 * Atomic by IDB semantics: a crash mid-transaction rolls back both.
 */
export const appendChunk = (db: IDBDatabase, session: SessionRecord, chunk: ChunkRecord): Promise<void> =>
    runWriteTransaction(db, [CHUNKS_STORE, SESSIONS_STORE], (tx) => {
        tx.objectStore(CHUNKS_STORE).put(chunk)
        tx.objectStore(SESSIONS_STORE).put(session)
    })

/**
 * Replaces every chunk for a session with a single merged chunk at sequence 0
 * and upserts the session row, all in one transaction. Atomic: either the
 * compacted state lands or the previous log is retained.
 */
export const compactSession = (db: IDBDatabase, session: SessionRecord, mergedChunk: ChunkRecord): Promise<void> =>
    runWriteTransaction(db, [CHUNKS_STORE, SESSIONS_STORE], (tx) => {
        const chunksStore = tx.objectStore(CHUNKS_STORE)
        const range = IDBKeyRange.bound([session.sessionKey, -Infinity], [session.sessionKey, Infinity])
        chunksStore.delete(range)
        chunksStore.put(mergedChunk)
        tx.objectStore(SESSIONS_STORE).put(session)
    })

/**
 * Range-deletes every chunk for `sessionKey` and deletes the session row in
 * one transaction. Used for schema mismatch wipes, corruption recovery, and
 * LRU eviction.
 */
export const deleteSession = (db: IDBDatabase, sessionKey: string): Promise<void> =>
    runWriteTransaction(db, [CHUNKS_STORE, SESSIONS_STORE], (tx) => {
        const range = IDBKeyRange.bound([sessionKey, -Infinity], [sessionKey, Infinity])
        tx.objectStore(CHUNKS_STORE).delete(range)
        tx.objectStore(SESSIONS_STORE).delete(sessionKey)
    })

/**
 * Returns the session keys to evict so that no more than `maxSessions` remain.
 * Walks the `sessions.lastWrittenAt` index ascending, skipping `excludeKey` if
 * supplied so the caller still gets `overflow` real eviction targets. Empty
 * array when under the cap.
 */
export const findSessionsToEvict = async (db: IDBDatabase, maxSessions: number, excludeKey?: string): Promise<string[]> => {
    return new Promise((resolve, reject) => {
        const tx = db.transaction(SESSIONS_STORE, 'readonly')
        const store = tx.objectStore(SESSIONS_STORE)
        const countRequest = store.count()

        countRequest.onsuccess = () => {
            const count = countRequest.result
            const overflow = count - maxSessions
            if (overflow <= 0) {
                resolve([])

                return
            }

            const keys: string[] = []
            const cursorRequest = store.index(LAST_WRITTEN_AT_INDEX).openKeyCursor(null, 'next')
            cursorRequest.onsuccess = () => {
                const cursor = cursorRequest.result
                if (!cursor || keys.length >= overflow) {
                    resolve(keys)

                    return
                }

                const primaryKey = cursor.primaryKey as string
                if (primaryKey !== excludeKey) {
                    keys.push(primaryKey)
                }
                cursor.continue()
            }
            cursorRequest.onerror = () => reject(cursorRequest.error)
        }

        countRequest.onerror = () => reject(countRequest.error)
    })
}
